/**
 * `/crm/deals` — the pipeline as a board, or every deal as a list.
 *
 * **The board** shows the open deals, one column per stage. Dragging a card to
 * another column moves the deal: the card lands at once, the request follows,
 * and the board reloads either way — on success for the new totals, on failure
 * to put the card back. Won and lost deals are not on it.
 *
 * **The list** shows deals of any status, paged and sortable, with search.
 *
 * Both are in the URL (`?view=list&status=won`), so Back returns to them.
 */
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { CRM_PERMISSIONS, type BoardColumnDto, type CrmBoardResponse, type DealDto, type DealStatus } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EmptyState } from '../../components/EmptyState';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { KanbanBoard } from '../../components/KanbanBoard';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useDebounced } from '../../hooks/useDebounced';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useDealActions } from './actions';
import { createDeal, getBoard, listDeals, listStages, moveDeal, today } from './api';
import { ConvertedValue } from './ConvertedValue';
import { DealCard } from './DealCard';
import { DealStageOrStatus } from './DealStatus';
import { formatDay, formatMoney, formatTotals } from './format';
import { BLANK_DEAL, createDealRequest, DealFields, dealIncomplete, type DealForm } from './forms';

type View = 'board' | 'list';
type StatusFilter = DealStatus | 'all';
const STATUSES: readonly StatusFilter[] = ['open', 'won', 'lost', 'all'];

/** The board with one deal already in another column — what the screen shows while the move is in flight. */
function movedLocally(board: CrmBoardResponse, dealId: number, stageId: number): CrmBoardResponse {
  const deal = board.columns.flatMap((column) => column.deals).find((one) => one.id === dealId);
  if (!deal) return board;
  return {
    columns: board.columns.map((column) => {
      const without = column.deals.filter((one) => one.id !== dealId);
      if (column.stage.id === stageId) {
        const landed = { ...deal, stage: { id: column.stage.id, name: column.stage.name }, rotten: false };
        return { ...column, deals: [...without, landed], count: column.count + 1 };
      }
      return without.length === column.deals.length ? column : { ...column, deals: without, count: column.count - 1 };
    }),
  };
}

/**
 * A column's totals in the caller's display currency — shown only when it adds
 * something: more than one currency, or one that is not the display currency.
 * A currency with no rate is named, never counted.
 */
function ConvertedTotal({ column }: { column: BoardColumnDto }) {
  const { t } = useTranslation('crm');
  const converted = column.converted;
  if (!converted || column.totals.every((total) => total.currency === converted.currency)) return null;

  return (
    <Typography
      variant="caption"
      component="div"
      color="text.secondary"
      title={converted.rateDate ? t('converted.atRateOf', { date: formatDay(converted.rateDate) }) : undefined}
    >
      ≈ {formatMoney(converted.value, converted.currency)}
      {column.stage.probability < 100 &&
        ` · ${t('board.weightedShort', { value: formatMoney(converted.weightedValue, converted.currency) })}`}
      {converted.missing.length > 0 && (
        <Typography variant="caption" component="div" color="warning.main">
          {t('converted.totalMissing', { currencies: converted.missing.join(', ') })}
        </Typography>
      )}
    </Typography>
  );
}

function Board({ version }: { version: number }) {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const board = useAsync(getBoard, [version]);
  const day = today();

  const move = useSubmit(async (deal: DealDto, stageId: number) => {
    if (board.data) board.setData(movedLocally(board.data, deal.id, stageId));
    try {
      return await moveDeal(deal.id, stageId);
    } finally {
      board.reload();
    }
  });

  return (
    <>
      {move.error && <ErrorState error={move.error} />}
      <AsyncContent state={board}>
        {({ columns }) =>
          columns.length === 0 ? (
            <EmptyState
              icon={<ViewKanbanOutlinedIcon fontSize="inherit" />}
              title={t('board.noStages')}
              description={t('board.noStagesDescription')}
              {...(can(CRM_PERMISSIONS.ADMIN)
                ? {
                    action: (
                      <Button component={RouterLink} to="/crm/stages" variant="outlined">
                        {t('stage.title')}
                      </Button>
                    ),
                  }
                : {})}
            />
          ) : (
            <KanbanBoard<DealDto>
              columns={columns.map((column) => ({
                id: column.stage.id,
                label: column.stage.name,
                summary: (
                  <>
                    {t('board.count', { count: column.count })}
                    {column.totals.length > 0 && ` · ${formatTotals(column.totals)}`}
                    {column.totals.length > 0 && column.stage.probability < 100 && (
                      <Typography variant="caption" component="div" color="text.disabled">
                        {t('board.weighted', {
                          probability: column.stage.probability,
                          value: formatTotals(column.totals, 'weightedValue'),
                        })}
                      </Typography>
                    )}
                    <ConvertedTotal column={column} />
                  </>
                ),
                items: column.deals,
                ...(column.hasMore
                  ? {
                      footer: (
                        <Link
                          component={RouterLink}
                          to={`?view=list&stageId=${column.stage.id}`}
                          variant="caption"
                          sx={{ px: 1 }}
                        >
                          {t('board.more', { count: column.count - column.deals.length })}
                        </Link>
                      ),
                    }
                  : {}),
              }))}
              itemKey={(deal) => deal.id}
              itemLabel={(deal) => deal.title}
              renderItem={(deal) => <DealCard deal={deal} today={day} />}
              emptyText={t('board.emptyColumn')}
              canMove={(deal) => deal.status === 'open' && can(CRM_PERMISSIONS.WRITE)}
              disabled={move.pending}
              onMove={(deal, _from, to) => void move.submit(deal, Number(to))}
            />
          )
        }
      </AsyncContent>
    </>
  );
}

function List({ version }: { version: number }) {
  const { t } = useTranslation('crm');
  const [params, setParams] = useSearchParams();
  const status = STATUSES.find((one) => one === params.get('status')) ?? 'open';
  const stageId = Number(params.get('stageId')) || undefined;
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim());

  const list = useListQuery({ defaultSort: 'createdAt', defaultOrder: 'desc', resetKey: `${status}:${stageId}:${q}` });
  const deals = useAsync(
    () => listDeals({ ...list.query, status, ...(stageId ? { stageId } : {}), ...(q ? { q } : {}) }),
    [list.key, status, stageId, q, version],
  );
  const actions = useDealActions({ onChanged: () => deals.reload() });

  const setStatus = (next: StatusFilter) => {
    const kept = new URLSearchParams(params);
    kept.set('status', next);
    setParams(kept);
  };

  return (
    <>
      <Tabs
        value={status}
        onChange={(_event, value: StatusFilter) => setStatus(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {STATUSES.map((one) => (
          <Tab key={one} value={one} label={t(`status.${one}`)} />
        ))}
      </Tabs>

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <SearchField
          value={term}
          onChange={setTerm}
          placeholder={t('deals.searchPlaceholder')}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        {stageId !== undefined && (
          <Chip
            label={t('deals.stageFilter')}
            onDelete={() => {
              const kept = new URLSearchParams(params);
              kept.delete('stageId');
              setParams(kept);
            }}
          />
        )}
      </Stack>

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={deals}>
        {({ deals: rows, page }) => (
          <EntityTable<DealDto>
            rows={rows}
            rowKey={(deal) => deal.id}
            list={list}
            page={page}
            empty={{
              icon: <HandshakeOutlinedIcon fontSize="inherit" />,
              title: t('deals.emptyTitle'),
              description: t('deals.emptyDescription'),
            }}
            columns={[
              {
                field: 'title',
                label: t('field.title'),
                sortable: true,
                render: (deal) => (
                  <Link component={RouterLink} to={`/crm/deals/${deal.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                    {deal.title}
                  </Link>
                ),
              },
              {
                field: 'organization',
                label: t('field.organization'),
                render: (deal) => deal.organization?.name ?? deal.person?.name ?? '',
              },
              {
                field: 'stage',
                label: t('field.stage'),
                width: 170,
                render: (deal) => <DealStageOrStatus deal={deal} />,
              },
              {
                field: 'value',
                label: t('field.value'),
                sortable: true,
                align: 'right',
                width: 150,
                render: (deal) => (
                  <>
                    {formatMoney(deal.value, deal.currency)}
                    <ConvertedValue converted={deal.converted} />
                  </>
                ),
              },
              {
                field: 'expectedCloseDate',
                label: t('field.expectedCloseDate'),
                sortable: true,
                width: 150,
                render: (deal) => formatDay(deal.expectedCloseDate),
              },
              { field: 'owner', label: t('field.owner'), width: 170, render: (deal) => deal.owner.name },
            ]}
            actions={actions.rowActions}
            actionsWidth={200}
          />
        )}
      </AsyncContent>
      {actions.dialogs}
    </>
  );
}

export default function DealsPage() {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'list' ? 'list' : 'board';
  const [version, setVersion] = useState(0);

  const crud = useCrudDialogs<DealDto, DealForm>({ blank: BLANK_DEAL, formFor: () => BLANK_DEAL });
  const stages = useAsync(listStages, [crud.isOpen], { enabled: crud.isOpen });
  const create = useSubmit(async () => {
    const result = await createDeal(createDealRequest(crud.form));
    setVersion((current) => current + 1);
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('deals.title')}
        description={t('deals.description')}
        actions={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_event, next: View | null) => {
                if (next) setParams(next === 'board' ? {} : { view: next });
              }}
            >
              <ToggleButton value="board" aria-label={t('deals.view.board')} title={t('deals.view.board')}>
                <ViewKanbanOutlinedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label={t('deals.view.list')} title={t('deals.view.list')}>
                <TableRowsOutlinedIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            {can(CRM_PERMISSIONS.WRITE) && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => crud.openCreate()}>
                {t('deals.new')}
              </Button>
            )}
          </Stack>
        }
      />

      {view === 'board' ? <Board version={version} /> : <List version={version} />}

      <FormDialog
        open={crud.isOpen}
        title={t('deals.createDialog.title')}
        submitLabel={t('deals.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={dealIncomplete(crud.form)}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        <DealFields form={crud.form} patch={crud.patch} stages={stages.data?.stages ?? []} />
      </FormDialog>
    </>
  );
}
